// The insurance plan's own "appoint a representative" form, chosen by the client's insurance.
// mode "client": shown inside the Client Signing Pass; the pass's own signature pad signs it (through onReady).
// mode "staff":  shown on the chart's representative form; staff can open, print, attach an ink-signed page, or have the client sign here.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SignaturePad from './SignaturePad';

const API = 'https://tcio-api.onrender.com';
const errOf = (e, d) => (e && e.response && e.response.data && e.response.data.error) || d;

export default function PlanRepFormPanel({ patientId, episodeId, mode = 'staff', clientNameDefault, onReady }) {
  const [info, setInfo] = useState(null);
  const [vals, setVals] = useState({});
  const [imgs, setImgs] = useState({});
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState(null);
  const [padKey, setPadKey] = useState(0);
  const [name, setName] = useState(clientNameDefault || '');
  const live = useRef({});
  const params = { patient_id: patientId, ...(episodeId ? { episode_id: episodeId } : {}) };

  const load = useCallback(async () => {
    const r = (await axios.get(`${API}/api/emr/plan-rep-form`, { params: { patient_id: patientId, ...(episodeId ? { episode_id: episodeId } : {}) } })).data;
    setInfo(r);
    if (r.form) { const v = {}; r.form.fields.filter(f => f.kind === 'text').forEach(f => { v[f.k] = f.value || ''; }); setVals(v); }
    if (r.form && r.form.mode === 'sign') {
      for (let n = 1; n <= r.form.pages; n += 1) {
        axios.get(`${API}/api/emr/plan-rep-form/page/${r.form.key}/${n}`).then(p => setImgs(m => ({ ...m, [n]: p.data.data }))).catch(() => {});
      }
    }
  }, [patientId, episodeId]);
  useEffect(() => { setInfo(null); setImgs({}); setErr(''); setMsg(''); load().catch(e => setErr(errOf(e, 'Could not load the insurance plan form'))); }, [load]);

  const form = info && info.form;
  const signing = info && info.signing;
  const signed = !!(signing && signing.signed);
  live.current = { form, signed, vals, params };

  // the Client Signing Pass calls this with the signature the client just drew
  useEffect(() => {
    if (!onReady) return undefined;
    onReady(async (image, clientName) => {
      const c = live.current;
      if (!c.form || c.form.mode !== 'sign' || c.signed) return;
      try { await axios.post(`${API}/api/emr/plan-rep-form/sign`, { ...c.params, values: c.vals, image_base64: image, client_name: clientName }); }
      catch (e) { if (!(e.response && e.response.status === 409)) throw e; } // already signed is fine: the pass may be retrying its own step
      live.current = { ...live.current, signed: true };
    });
    return () => onReady(null);
  }, [onReady]);

  const openPdf = async () => {
    setErr(''); const w = window.open('', '_blank');
    try {
      const r = await axios.get(`${API}/api/emr/plan-rep-form/pdf`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (w) w.location = url; else window.location.assign(url);
      if (form.mode === 'paper') load().catch(() => {});
    } catch (e) { if (w) w.close(); setErr('Could not open the form'); }
  };
  const openScan = async () => {
    setErr(''); const w = window.open('', '_blank');
    try { const r = await axios.get(`${API}/api/emr/plan-rep-form/scan`, { params }); if (w) w.location = r.data.url; else window.location.assign(r.data.url); }
    catch (e) { if (w) w.close(); setErr(errOf(e, 'Could not open the signed page')); }
  };
  const attach = async (file) => {
    if (!file) return; setErr(''); setMsg(''); setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('patient_id', patientId); if (episodeId) fd.append('episode_id', episodeId);
      await axios.post(`${API}/api/emr/plan-rep-form/scan`, fd); setMsg('Signed page attached.'); await load();
    } catch (e) { setErr(errOf(e, 'Could not attach that file')); }
    setBusy(false);
  };
  const signHere = async () => {
    setErr(''); setMsg('');
    if (!name.trim()) { setErr('Enter the client name first'); return; }
    if (!sig) { setErr('Please sign in the box'); return; }
    setBusy(true);
    try { await axios.post(`${API}/api/emr/plan-rep-form/sign`, { ...params, values: vals, image_base64: sig, client_name: name.trim() }); setSig(null); setPadKey(k => k + 1); setMsg('Signed.'); await load(); }
    catch (e) { setErr(errOf(e, 'Could not save the signature')); }
    setBusy(false);
  };
  const redo = async () => {
    const reason = window.prompt('Why does this form need to be signed again? The current one is kept on file.'); if (!reason || !reason.trim()) return;
    setErr(''); setMsg(''); setBusy(true);
    try { await axios.post(`${API}/api/emr/plan-rep-form/redo`, { ...params, reason: reason.trim() }); await load(); }
    catch (e) { setErr(errOf(e, 'Could not do that')); }
    setBusy(false);
  };

  if (!info) return err ? <div style={S.err}>{err}</div> : null;
  if (!form) {
    if (mode === 'client') return null;
    return <div style={S.box}><div style={S.small}>{info.payer ? `There is no plan-specific representative form on file for ${info.payer}. The form below covers it.` : 'No insurance is on this stay yet, so no plan form was chosen. The form below covers it.'}</div></div>;
  }

  const shown = (f) => { const v = String(vals[f.k] || ''); return f.fmt === 'MMDDYYYY' ? v.replace(/\D/g, '') : v; };
  const pages = []; for (let n = 1; n <= form.pages; n += 1) pages.push(n);
  const textFields = form.fields.filter(f => f.kind === 'text');

  return (
    <div style={S.box}>
      <div style={S.kicker}>{mode === 'client' ? 'Your insurance plan’s form' : 'Insurance plan form'}{info.payer ? ` · ${info.payer}` : ''}</div>
      <div style={S.title}>{form.title}</div>

      {form.mode === 'paper' ? (
        <div>
          <div style={S.note}>{form.paper_note}</div>
          {signing && signing.printed_at && <div style={S.small}>Printed {signing.printed_at}{signing.printed_by_name ? ` by ${signing.printed_by_name}` : ''}.</div>}
          {signing && signing.has_scan && <div style={S.ok}>{'✓'} Signed page attached{signing.recorded_by_name ? ` by ${signing.recorded_by_name}` : ''}{signing.signed_at ? `, ${signing.signed_at}` : ''}.</div>}
          <div style={S.row}>
            <button type="button" onClick={openPdf} style={S.btn}>Print the form</button>
            <label style={{ ...S.btnLight, opacity: busy ? 0.6 : 1 }}>
              {signing && signing.has_scan ? 'Replace the signed page' : 'Attach the signed page'}
              <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} disabled={busy} onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; attach(f); }} />
            </label>
            {signing && signing.has_scan && <button type="button" onClick={openScan} style={S.btnLight}>Open the signed page</button>}
          </div>
        </div>
      ) : signed ? (
        <div>
          <div style={S.ok}>{'✓'} Signed by {signing.signer_name || 'the client'}{signing.signed_at ? ` on ${signing.signed_at}` : ''}.</div>
          <div style={S.row}>
            <button type="button" onClick={openPdf} style={S.btn}>Open the signed form</button>
            {mode === 'staff' && <button type="button" onClick={redo} disabled={busy} style={S.btnLight}>Sign again</button>}
          </div>
        </div>
      ) : (
        <div>
          <div style={S.small}>{mode === 'client' ? 'We filled in what we have on file. Please check each line and fix anything that is wrong. Your signature below signs this form too.' : 'Filled from the chart. Correct anything that is wrong before the client signs.'}</div>
          <div style={S.grid}>
            {textFields.map(f => (
              <label key={f.k} style={S.field}>
                <span style={S.label}>{f.label}{f.optional ? ' (optional)' : ''}</span>
                <input value={vals[f.k] || ''} onChange={e => setVals(v => ({ ...v, [f.k]: e.target.value }))} style={S.input} />
              </label>
            ))}
          </div>
          <div style={S.note}>{form.date_note}</div>
          <div style={S.pages}>
            {pages.map(n => (
              <svg key={n} viewBox="0 0 612 792" style={S.page}>
                {imgs[n] ? <image href={imgs[n]} x="0" y="0" width="612" height="792" /> : <text x="306" y="396" textAnchor="middle" fontSize="12" fill="#94a3b8">Loading page {n}{'…'}</text>}
                {imgs[n] && textFields.filter(f => f.page === n).map(f => { const v = shown(f); const tight = v.length * f.size * 0.52 > f.w;
                  return <text key={f.k} x={f.x} y={f.y + f.h - 3} fontSize={f.size} fontFamily="Helvetica, Arial, sans-serif" fill="#0f2f57" {...(tight ? { textLength: f.w, lengthAdjust: 'spacingAndGlyphs' } : {})}>{v}</text>; })}
              </svg>
            ))}
          </div>
          {mode === 'staff' && (
            <div style={{ marginTop: 12 }}>
              <div style={S.label}>CLIENT NAME</div>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="First Last" style={{ ...S.input, maxWidth: 320 }} />
              <div style={{ ...S.label, marginTop: 10 }}>CLIENT SIGNS BELOW</div>
              <SignaturePad key={padKey} onChange={setSig} height={130} />
              <div style={S.row}>
                <button type="button" onClick={signHere} disabled={busy} style={S.btn}>{busy ? 'Saving…' : 'Save the signed plan form'}</button>
                <button type="button" onClick={openPdf} style={S.btnLight}>Open the form</button>
              </div>
            </div>
          )}
        </div>
      )}
      {msg && <div style={S.ok}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}
    </div>
  );
}

const S = {
  box: { border: '1.5px solid #c7d7ea', background: '#f5f9fd', borderRadius: 10, padding: '14px 16px', margin: '12px 0' },
  kicker: { fontSize: 11, fontWeight: 800, color: '#4479B0', letterSpacing: '0.05em', textTransform: 'uppercase' },
  title: { fontSize: 15, fontWeight: 800, color: '#1e293b', margin: '3px 0 8px' },
  small: { fontSize: 13, color: '#475569', lineHeight: 1.5, margin: '4px 0' },
  note: { fontSize: 13, color: '#7c4a03', background: '#fef7e6', border: '1px solid #f3dfae', borderRadius: 8, padding: '8px 10px', margin: '8px 0', lineHeight: 1.5 },
  ok: { fontSize: 13.5, fontWeight: 700, color: '#15803d', margin: '6px 0' },
  err: { fontSize: 13, fontWeight: 700, color: '#dc2626', margin: '6px 0' },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, margin: '10px 0' },
  field: { display: 'flex', flexDirection: 'column', gap: 3 },
  label: { fontSize: 11.5, fontWeight: 700, color: '#6b7280' },
  input: { width: '100%', padding: '9px 10px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14.5, boxSizing: 'border-box', background: '#fff' },
  pages: { maxHeight: 420, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#e5e7eb', padding: 6 },
  page: { display: 'block', width: '100%', background: '#fff', marginBottom: 6 },
  btn: { background: '#4479B0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' },
  btnLight: { background: '#fff', color: '#336090', border: '1.5px solid #b9cce2', borderRadius: 8, padding: '9px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
};
